import React, { useEffect, useState } from 'react';
import { ClientResponse, Preferences } from '../../../redux/api/customer/customer.model';
import '../../CreateInvoice/CreateInvoice.scss';
import { useGetCustomerByIdQuery } from '../../../redux/api/customer/customerApi';
import useNotifications from '../../../providers/Notifications/Notifications.provider';
import { LoadLocation } from '../../../redux/api/load/load.model';
import TwEditCompaniesModal from '../../Companies/EditCompaniesModal/TwEditCompaniesModal';
import { CompaniesEntities } from '../../Companies/Companies';

export interface BillToData {
  placeId?: string;
  addressLine?: string;
  city?: string;
  state?: string;
  zip?: string;
  placeName?: string;
  clientName: string;
  clientEmail: string;
  clientPhone?: string;
  clientId: string;
  preferences: Preferences | null;
  externalCustomerId?: string;
}

export interface BillToProps {
  clientId: string;
  onBillToEdited?: (billData: BillToData) => void;
  onNewLocation?: (location: LoadLocation) => void;
}

const BillTo: React.FC<BillToProps> = ({
  clientId,
  onBillToEdited,
  onNewLocation
}): JSX.Element => {
  const [client, setClient] = useState<ClientResponse | undefined>(undefined);

  const [clientName, setClientName] = useState<string>('');
  const [clientEmail, setClientEmail] = useState<string>('');

  const [clientPhone, setClientPhone] = useState<string>('');

  // Edit client dialog
  const [editClientOpen, setEditClientOpen] = useState(false);

  // Manually added location
  const [clientLocationOpen, setClientLocationOpen] = useState(false);
  const [clientCreatedLocation, setClientCreatedLocation] = useState<LoadLocation | undefined>(
    undefined
  );

  // Google place data

  // RTK Clients
  const { data: clientData } = useGetCustomerByIdQuery(clientId);

  const { addNotification } = useNotifications();

  const handleClientEditOpen = (): void => {
    setEditClientOpen(true);
  };

  const handleClientEditClose = (): void => {
    setEditClientOpen(false);
  };

  useEffect(() => {
    if (clientData) {
      try {
        setClient(clientData);
      } catch (error) {
        addNotification({ message: "Couldn't get customer info", variant: 'error' });
      }
    }
  }, [clientData]);

  useEffect(() => {
    // If all data is present for the given client, we return the mandatory fields
    // to prevent errors in validations when invoice submits
    if (client?.name || client?.billing_email) {
      const billToData: BillToData = {
        clientName: client?.name,
        clientEmail: client?.billing_email || '',
        clientId: clientId,
        clientPhone: client?.primary_phone || '',
        preferences: client.preferences,
        externalCustomerId: client?.external_customer_id
      };

      onBillToEdited && onBillToEdited(billToData);
    }

    client?.name && setClientName(client?.name);
    client?.primary_phone && setClientPhone(client?.primary_phone);
    setClientEmail(client?.billing_email || '');
  }, [client]);

  useEffect(() => {
    const billToData: BillToData = {
      clientName: clientName,
      clientEmail: clientEmail,
      clientPhone: clientPhone,
      clientId: clientId,
      preferences: client?.preferences || null,
      externalCustomerId: client?.external_customer_id
    };

    // If address is selected from autocomplete, this returns client data to be saved
    // when Invoice is submtited
    onBillToEdited && onBillToEdited(billToData);
  }, [clientName, clientEmail, clientPhone]);

  useEffect(() => {
    clientCreatedLocation && onNewLocation && onNewLocation(clientCreatedLocation);
  }, [clientCreatedLocation]);

  return (
    <div className="create-invoice__bill-to">
      <div className="create-invoice__bill-header">
        <span className="create-invoice__bill-title">Bill to</span>
        <span className="create-invoice__bill-edit-link" onClick={handleClientEditOpen}>
          Edit customer info
        </span>
      </div>
      <span className="create-invoice__bill-name">{client?.name}</span>
      {client?.location?.full_address && (
        <span className="create-invoice__bill-description">{client?.location?.full_address}</span>
      )}
      <span className="create-invoice__bill-description">{client?.billing_email}</span>
      <span className="create-invoice__bill-description">{client?.primary_phone}</span>

      <TwEditCompaniesModal
        entityId={clientId}
        open={editClientOpen}
        onClose={handleClientEditClose}
        onSuccess={(updatedClient): void => setClient(updatedClient)}
        entity={CompaniesEntities.customer}
      />
    </div>
  );
};

export default BillTo;
